import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import imageCompression from "browser-image-compression";
import { storage } from "../../firebase";
import { contentService } from "../../services/contentService";
import { TEAM_DEFAULTS } from "../../pages/Team";

const makeId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

// Storage delete that tolerates files already removed from the bucket
const removeFromStorage = async (path) => {
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    if (err.code !== "storage/object-not-found") throw err;
  }
};

const MemberPhoto = ({ member, onPhotoChange, onError }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      onError(`${file.name} is not a valid image.`);
      e.target.value = "";
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      onError(`${file.name} is too large (max 30MB).`);
      e.target.value = "";
      return;
    }

    setBusy(true);
    try {
      let fileToUpload = file;
      if (file.size > 1024 * 1024) {
        try {
          fileToUpload = await imageCompression(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1600,
            useWebWorker: true,
            fileType: "image/jpeg",
          });
        } catch (err) {
          console.warn("Compression failed, uploading original:", err);
        }
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filename = `${Date.now()}_${safeName}`;
      const path = `team/${member.id}/${filename}`;

      const snapshot = await uploadBytes(ref(storage, path), fileToUpload);
      const url = await getDownloadURL(snapshot.ref);

      // Clean up the photo being replaced
      const oldPath = member.photo?.path;
      onPhotoChange({ url, path, filename });
      if (oldPath && oldPath !== path) await removeFromStorage(oldPath);
    } catch (err) {
      console.error("Team photo upload failed:", err);
      onError(`Upload failed: ${err.message}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove this photo?")) return;
    setBusy(true);
    try {
      await removeFromStorage(member.photo?.path);
      onPhotoChange(null);
    } catch (err) {
      onError(`Failed to remove photo: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Form.Control
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleSelect}
        style={{ display: "none" }}
      />

      <div
        className="border rounded overflow-hidden bg-light mb-2 d-flex align-items-center justify-content-center"
        style={{ height: "180px" }}
      >
        {member.photo?.url ? (
          <img
            src={member.photo.url}
            alt={member.name || "Team member"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span className="text-muted small">No photo</span>
        )}
      </div>

      <div className="d-grid gap-2">
        <Button
          variant="outline-primary"
          size="sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? "Working..." : member.photo?.url ? "Replace Photo" : "Upload Photo"}
        </Button>
        {member.photo?.url && (
          <Button
            variant="outline-danger"
            size="sm"
            disabled={busy}
            onClick={handleRemove}
          >
            Remove Photo
          </Button>
        )}
      </div>
    </>
  );
};

const TeamManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [h1, setH1] = useState(TEAM_DEFAULTS.h1);
  const [intro, setIntro] = useState(TEAM_DEFAULTS.intro);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await contentService.getPageContent("team");
        if (data) {
          if (data.h1) setH1(data.h1);
          if (Array.isArray(data.intro) && data.intro.length > 0)
            setIntro(data.intro);
          if (Array.isArray(data.members)) setMembers(data.members);
        }
      } catch (err) {
        setMsg({ type: "danger", text: "Failed to load team content." });
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const showError = (text) => setMsg({ type: "danger", text });

  // --- Paragraph handlers ---
  const updateParagraph = (index, value) =>
    setIntro((prev) => prev.map((p, i) => (i === index ? value : p)));

  const addParagraph = () => setIntro((prev) => [...prev, ""]);

  const deleteParagraph = (index) =>
    setIntro((prev) => prev.filter((_, i) => i !== index));

  // --- Member handlers ---
  const updateMember = (id, changes) =>
    setMembers((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...changes } : m))
    );

  const addMember = () =>
    setMembers((prev) => [
      ...prev,
      { id: makeId(), name: "", role: "", bio: "", photo: null },
    ]);

  const deleteMember = async (member) => {
    if (
      !window.confirm(
        `Delete ${member.name || "this team member"}? This also removes their photo.`
      )
    )
      return;
    try {
      await removeFromStorage(member.photo?.path);
    } catch (err) {
      showError(`Photo could not be deleted: ${err.message}`);
    }
    setMembers((prev) => prev.filter((m) => m.id !== member.id));
  };

  const moveMember = (index, direction) => {
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= members.length) return;
    setMembers((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await contentService.updatePageContent("team", {
        h1,
        intro: intro.filter((p) => p.trim() !== ""),
        members,
      });
      setMsg({ type: "success", text: "Meet the Team page updated!" });
    } catch (err) {
      setMsg({ type: "danger", text: `Failed to save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Meet the Team Page</span>
        <Button size="sm" disabled={saving} onClick={handleSave}>
          {saving ? <Spinner size="sm" /> : "Save Changes"}
        </Button>
      </Card.Header>
      <Card.Body>
        {msg && (
          <Alert variant={msg.type} onClose={() => setMsg(null)} dismissible>
            {msg.text}
          </Alert>
        )}

        {/* Intro Section */}
        <h5 className="mb-3 text-primary">Page Intro</h5>
        <Form.Group className="mb-4">
          <Form.Label>Page Title (H1)</Form.Label>
          <Form.Control
            type="text"
            value={h1}
            onChange={(e) => setH1(e.target.value)}
          />
        </Form.Group>

        {intro.map((paragraph, index) => (
          <Form.Group className="mb-3" key={index}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <Form.Label className="mb-0">Paragraph {index + 1}</Form.Label>
              <Button
                variant="link"
                size="sm"
                className="text-danger p-0 text-decoration-none"
                onClick={() => deleteParagraph(index)}
              >
                Delete
              </Button>
            </div>
            <Form.Control
              as="textarea"
              rows={3}
              value={paragraph}
              onChange={(e) => updateParagraph(index, e.target.value)}
            />
          </Form.Group>
        ))}

        <Button
          variant="outline-secondary"
          size="sm"
          className="mb-4"
          onClick={addParagraph}
        >
          + Add Paragraph
        </Button>

        {/* Members Section */}
        <hr />
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 text-primary">
            Team Members ({members.length})
          </h5>
          <Button variant="outline-primary" size="sm" onClick={addMember}>
            + Add Team Member
          </Button>
        </div>

        {members.length === 0 ? (
          <div className="text-center py-4 text-muted border rounded">
            <p className="mb-1">No team members yet.</p>
            <small>
              Click "Add Team Member" to add a photo and description.
            </small>
          </div>
        ) : (
          members.map((member, index) => (
            <Card className="mb-3 bg-light" key={member.id}>
              <Card.Body>
                <Row>
                  <Col md={3} className="mb-3 mb-md-0">
                    <MemberPhoto
                      member={member}
                      onPhotoChange={(photo) =>
                        updateMember(member.id, { photo })
                      }
                      onError={showError}
                    />
                  </Col>

                  <Col md={9}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong className="text-muted small">
                        Member {index + 1}
                      </strong>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={index === 0}
                          onClick={() => moveMember(index, "up")}
                          title="Move up"
                        >
                          ↑
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={index === members.length - 1}
                          onClick={() => moveMember(index, "down")}
                          title="Move down"
                        >
                          ↓
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => deleteMember(member)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. A. Chris Fawzy"
                            value={member.name || ""}
                            onChange={(e) =>
                              updateMember(member.id, { name: e.target.value })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Role / Title</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. Founder & Owner"
                            value={member.role || ""}
                            onChange={(e) =>
                              updateMember(member.id, { role: e.target.value })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group>
                      <Form.Label>
                        Description{" "}
                        <span className="text-muted small">
                          (appears beneath the photo)
                        </span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="A short description of this team member..."
                        value={member.bio || ""}
                        onChange={(e) =>
                          updateMember(member.id, { bio: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))
        )}

        <div className="mt-4">
          <Button disabled={saving} onClick={handleSave}>
            {saving ? <Spinner size="sm" /> : "Save Changes"}
          </Button>
          <span className="text-muted small ms-3">
            Photos upload immediately — text changes require saving.
          </span>
        </div>
      </Card.Body>
    </Card>
  );
};

export default TeamManagement;
